/**
 * Newsletter form submission and event delegation
 * @module lib/newsletter/form
 */

const { createNewsletterIntent } = require('./intent');
const identity = require('../identity');
const intents = new WeakMap();
function intentFor(form) {
  if (!intents.has(form)) intents.set(form, createNewsletterIntent(() => identity));
  return intents.get(form);
}

const { logWarn } = require('../logger');
const { resolveNewsletterContext } = require('../utils/brandContext');
const { buildNewsletterSubscribeUrl } = require('./urls');
const { ensureNewsletterNoticeStyles } = require('./styles');
const { NOTICE_VERSION, NOTICE_COPY } = require('../../../../../shared/newsletterNotice.cjs');

// Native forms come from RebelMouse templates. The SDK adds the welcome-email
// notice to each submittable form as soon as it sees one (page ready, focus,
// typing), so the reader can read it before submitting. A template may supply
// its own .milla-newsletter-notice; an empty one gets the standard copy.
function ensureNewsletterNotice(form) {
  if (!form || typeof form.querySelector !== 'function') return null;
  if (!form.querySelector('[data-action="newsletter-submit"]')) return null;
  let notice = form.querySelector('.milla-newsletter-notice');
  if (!notice) {
    notice = form.ownerDocument.createElement('div');
    notice.className = 'milla-newsletter-notice';
    const anchor = form.querySelector('.milla-newsletter-message');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(notice, anchor);
    else form.appendChild(notice);
    ensureNewsletterNoticeStyles();
  }
  if (!(notice.textContent || '').trim()) {
    notice.textContent = NOTICE_COPY[resolveNewsletterContext().language === 'fr' ? 'fr' : 'en'];
  }
  return notice;
}

// The sign-up records the notice only when it was inside the form and
// rendered (not hidden by host CSS) when the reader submitted.
function isNewsletterNoticeShown(form) {
  const notice = form.querySelector('.milla-newsletter-notice');
  return Boolean(notice && (notice.textContent || '').trim() && notice.getClientRects().length > 0);
}

// Email validation helpers
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const spamPatterns = [
  /^test@test\./i,
  /^fake@/i,
  /^spam@/i,
  /^asdf+@/i,
  /^aaa+@/i,
  /^123+@/i,
  /^xxx+@/i,
  /example\.com$/i,
  /example\.org$/i,
  /mailinator\.com$/i,
  /tempmail\./i,
  /throwaway\./i,
  /guerrillamail\./i,
  /10minutemail\./i
];

function validateEmail(email, ctx) {
  const invalidEmailMsg = ctx.language === 'fr'
    ? 'Entrez une adresse courriel valide'
    : 'Enter a valid email address';

  // Length validation (RFC 5321)
  if (email.length < 5 || email.length > 254) {
    return { valid: false, message: invalidEmailMsg };
  }

  // Email format validation
  if (!emailRegex.test(email)) {
    return { valid: false, message: invalidEmailMsg };
  }

  // Block consecutive dots
  if (/\.{2,}/.test(email)) {
    return { valid: false, message: invalidEmailMsg };
  }

  // Anti-spam patterns
  const lowerEmail = email.toLowerCase();
  for (const pattern of spamPatterns) {
    if (pattern.test(lowerEmail)) {
      return { valid: false, message: invalidEmailMsg };
    }
  }

  return { valid: true, message: null };
}

function submitNewsletterForm(formElement, location) {
  if (!formElement) return;
  const emailInput = formElement.querySelector('input[type="email"]');
  const button = formElement.querySelector('[data-action="newsletter-submit"]');
  const messageEl = formElement.querySelector('.milla-newsletter-message');

  if (!emailInput || !button) {
    return;
  }

  // Measured before ensuring the notice: one added by this click was not
  // seen before submitting, so only the next submission can record it.
  const noticeShown = isNewsletterNoticeShown(formElement);
  ensureNewsletterNotice(formElement);
  const ctx = resolveNewsletterContext();
  const email = (emailInput.value || '').trim();

  if (!email) {
    if (messageEl) {
      messageEl.textContent = ctx.language === 'fr'
        ? 'Entrez votre courriel'
        : 'Enter your email to join';
      messageEl.className = 'milla-newsletter-message milla-newsletter-message-error';
    }
    return;
  }

  const validation = validateEmail(email, ctx);
  if (!validation.valid) {
    if (messageEl) {
      messageEl.textContent = validation.message;
      messageEl.className = 'milla-newsletter-message milla-newsletter-message-error';
    }
    return;
  }

  const url = buildNewsletterSubscribeUrl();
  const payload = {
    email,
    newsletterId: ctx.id,
    language: ctx.language || 'en',
    sourceUrl: typeof window !== 'undefined' ? window.location.href : null,
    location: location || null
  };
  if (noticeShown) payload.publisherOnboardingNotice = NOTICE_VERSION;

  button.disabled = true;
  emailInput.disabled = true;
  if (messageEl) {
    messageEl.textContent = ctx.language === 'fr' ? 'Inscription…' : 'Subscribing…';
    messageEl.className = 'milla-newsletter-message milla-newsletter-message-pending';
  }

  const intent = intentFor(formElement);
  payload.submissionId = intent.submissionId({ email, newsletterId: ctx.id, language: ctx.language });
  intent.acquire().then((token) => {
    if (token) payload.identityHandoff = token;
    return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
    });
  })
    .then((res) => {
      if (!res.ok) throw new Error('Newsletter request failed');
      return res.json();
    })
    .then((data) => {
      if (!data || data.success === false) {
        const defaultMsg = ctx.language === 'fr'
          ? `Impossible de s'inscrire pour le moment`
          : 'Unable to subscribe right now';
        const msg = data && (data.message || data.error)
          ? data.message || data.error
          : defaultMsg;
        if (messageEl) {
          messageEl.textContent = msg;
          messageEl.className = 'milla-newsletter-message milla-newsletter-message-error';
        }
        button.disabled = false;
        emailInput.disabled = false;
        return;
      }

      if (messageEl) {
        messageEl.textContent = ctx.language === 'fr'
          ? 'Ta demande est enregistrée. Tes préférences existantes seront respectées.'
          : "Your request is saved. Existing email preferences will be respected.";
        messageEl.className = 'milla-newsletter-message milla-newsletter-message-success';
      }
    })
    .catch((err) => {
      logWarn('Newsletter subscribe failed', err);
      if (messageEl) {
        messageEl.textContent = ctx.language === 'fr'
          ? `Impossible de s'inscrire pour le moment`
          : 'Unable to subscribe right now';
        messageEl.className = 'milla-newsletter-message milla-newsletter-message-error';
      }
      button.disabled = false;
      emailInput.disabled = false;
    });
}

let newsletterDelegationInitialized = false;

function handleNewsletterClick(event) {
  if (!event || !event.target) return;
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return;

  const button = target.closest('[data-action="newsletter-submit"]');
  if (!button) return;

  const form = button.closest('.milla-newsletter-form');
  if (!form) return;

  const location = form.getAttribute('data-location') || null;
  submitNewsletterForm(form, location);
}

function handleNewsletterEmailBlur(event) {
  if (!event || !event.target) return;
  const target = event.target;
  if (!target || target.type !== 'email') return;
  if (!target.closest || typeof target.closest !== 'function') return;

  const form = target.closest('.milla-newsletter-form');
  if (!form) return;

  const messageEl = form.querySelector('.milla-newsletter-message');
  const email = (target.value || '').trim();
  const ctx = resolveNewsletterContext();

  // Don't show error on blur if empty - wait for submit
  if (!email) {
    return;
  }

  const validation = validateEmail(email, ctx);
  if (!validation.valid) {
    if (messageEl) {
      messageEl.textContent = validation.message;
      messageEl.className = 'milla-newsletter-message milla-newsletter-message-error';
    }
    return;
  }

  // Clear error if valid
  if (messageEl) {
    messageEl.textContent = '';
    messageEl.className = 'milla-newsletter-message';
  }
}

function ensureNewsletterEventDelegation() {
  if (typeof document === 'undefined' || newsletterDelegationInitialized) {
    return;
  }

  const prefetch = (event) => {
    const form = event.target?.type === 'email' && event.target.closest?.('.milla-newsletter-form');
    if (!form) return;
    ensureNewsletterNotice(form);
    intentFor(form).prefetchOnIntent();
  };
  const addNotices = () => {
    try { document.querySelectorAll('.milla-newsletter-form').forEach(ensureNewsletterNotice); }
    catch (err) { logWarn('Failed to add newsletter notices', err); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', addNotices, { once: true });
  else addNotices();
  document.addEventListener('focusin', prefetch);
  document.addEventListener('input', prefetch);
  document.addEventListener('click', handleNewsletterClick);
  // Use focusout instead of blur - focusout bubbles, blur does not
  document.addEventListener('focusout', handleNewsletterEmailBlur);
  newsletterDelegationInitialized = true;
}

// Auto-initialize event delegation
if (typeof document !== 'undefined') {
  try {
    ensureNewsletterEventDelegation();
  } catch (err) {
    logWarn('Failed to initialize newsletter event delegation', err);
  }
}

module.exports = {
  submitNewsletterForm,
  ensureNewsletterEventDelegation
};
